import React from 'react';
import { Text, View } from '@react-pdf/renderer';

/**
 * RTL-safe text for the nutrition PDF.
 *
 * @react-pdf/renderer lays text out with an LTR base direction and offers no way to
 * change it (`direction: 'rtl'` on a Text garbles the run; on a View it does nothing
 * for text). With an LTR base, anything that isn't a strong RTL character drifts to
 * the wrong side of a Hebrew phrase:
 *
 *   "10 מ"ל"          -> the number lands left of the unit  (reads "מ"ל 10")
 *   "משקל תינוק:"      -> the colon lands right of the words (reads ":משקל תינוק")
 *   "קלוריות (kcal)"   -> "(kcal)" lands right of the Hebrew (reads "(kcal) קלוריות")
 *
 * So we don't let the engine order the phrase at all: the string is tokenised and the
 * tokens are laid out in a `row-reverse` flex container, which puts the first token on
 * the right deterministically. Each token is short enough to be unambiguous on its own.
 *
 * Two details that matter, both learned the hard way:
 *  - **No `flexWrap`.** With wrapping enabled this container collapses inside
 *    percentage-width table cells and its tokens vanish from the page entirely.
 *  - **Consecutive Latin words are merged** into one token, otherwise row-reverse would
 *    reverse them ("PAGIM D5P3EL" -> "D5P3EL PAGIM").
 *
 * Pure-Latin / numeric strings skip all of this and render as a plain <Text>.
 */

const HEBREW = /[֐-׿]/;
const LATIN_TOKEN = /^[A-Za-z0-9][A-Za-z0-9.\-/]*$/;
// Punctuation that trails a word and must become its own token so it can be placed
// to the *left* of that word rather than drifting to its right.
const TRAILING_PUNCT = /^(.+?)([:,.;!?]+)$/;

const SPACE = ' ';

const tokenize = (input: string): string[] => {
    const tokens: string[] = [];
    const words = input.split(SPACE);

    words.forEach((word, index) => {
        if (word !== '') {
            const match = TRAILING_PUNCT.exec(word);
            if (match) {
                tokens.push(match[1]);
                tokens.push(match[2]);
            } else {
                tokens.push(word);
            }
        }
        if (index < words.length - 1) tokens.push(SPACE);
    });

    // Merge "Latin space Latin" back into a single token so its word order survives.
    const merged: string[] = [];
    for (let i = 0; i < tokens.length; i++) {
        const prev = merged[merged.length - 1];
        const next = tokens[i + 1];
        if (
            tokens[i] === SPACE &&
            prev !== undefined && LATIN_TOKEN.test(prev) &&
            next !== undefined && LATIN_TOKEN.test(next)
        ) {
            merged[merged.length - 1] = `${prev} ${next}`;
            i += 1;
            continue;
        }
        merged.push(tokens[i]);
    }

    return merged;
};

// Box-model props belong to the wrapping container, not to each token — applied
// per token, a `marginLeft` would open a gap between every word and its punctuation.
const CONTAINER_PROPS = [
    'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
    'marginHorizontal', 'marginVertical',
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'paddingHorizontal', 'paddingVertical',
    'width', 'maxWidth', 'minWidth', 'flex', 'flexGrow', 'flexShrink', 'alignSelf',
];

const splitStyle = (style: any): { container: any; text: any } => {
    const flat = Array.isArray(style) ? Object.assign({}, ...style.filter(Boolean)) : (style || {});
    const container: any = {};
    const text: any = {};
    Object.keys(flat).forEach((key) => {
        if (CONTAINER_PROPS.includes(key)) container[key] = flat[key];
        else text[key] = flat[key];
    });
    return { container, text };
};

interface RtlTextProps {
    children: string | number | null | undefined;
    style?: any;
    /** Where the phrase sits in its container. Defaults to the right (RTL start). */
    align?: 'right' | 'center';
}

export const RtlText: React.FC<RtlTextProps> = ({ children, style, align = 'right' }) => {
    const value = children === 0 ? '0' : children;
    if (value === null || value === undefined || value === '') {
        return <Text style={style}> </Text>;
    }

    const asString = String(value);
    if (!HEBREW.test(asString)) {
        // Nothing RTL in it — let the engine handle it as-is.
        return <Text style={style}>{asString}</Text>;
    }

    const tokens = tokenize(asString);
    const { container, text } = splitStyle(style);

    return (
        <View
            style={{
                ...container,
                flexDirection: 'row-reverse',
                justifyContent: align === 'center' ? 'center' : 'flex-start',
                alignItems: 'baseline',
            }}
        >
            {tokens.map((token, index) => (
                <Text key={index} style={text}>{token}</Text>
            ))}
        </View>
    );
};

export default RtlText;
